import { Button, IconButton, Fab } from "@mui/material";
import { styled } from "@mui/material/styles";
import { ReactNode } from "react";

// Primary Action Button - For main actions like Save, Submit, etc.
export const PrimaryButton = styled(Button)(({ theme }) => ({
  borderRadius: theme.spacing(1),
  padding: theme.spacing(1.5, 3),
  fontWeight: 600,
  textTransform: "none",
  fontSize: "1rem",
  transition: "all 0.3s ease-in-out",
  background: `linear-gradient(135deg, ${theme.palette.primary.main} 0%, rgba(90, 100, 119, 0.8) 100%)`,
  color: "#FFFFFF",
  border: "none",
  boxShadow: "0 4px 12px rgba(90, 100, 119, 0.3)",
  "&:hover": {
    transform: "translateY(-2px)",
    boxShadow: "0 6px 20px rgba(90, 100, 119, 0.4)",
    background: `linear-gradient(135deg, rgba(90, 100, 119, 0.9) 0%, ${theme.palette.primary.main} 100%)`,
  },
  "&:active": {
    transform: "translateY(0)",
    boxShadow: "0 2px 8px rgba(90, 100, 119, 0.2)",
  },
  "&:disabled": {
    background: "rgba(90, 100, 119, 0.3)",
    color: "rgba(255, 255, 255, 0.6)",
    transform: "none",
    boxShadow: "none",
  },
}));

// Secondary Button - For secondary actions like Cancel, Back, etc.
export const SecondaryButton = styled(Button)(({ theme }) => ({
  borderRadius: theme.spacing(1),
  padding: theme.spacing(1.5, 3),
  fontWeight: 600,
  textTransform: "none",
  fontSize: "1rem",
  transition: "all 0.3s ease-in-out",
  background: "transparent",
  border: `2px solid ${theme.palette.primary.main}`,
  color: theme.palette.primary.main,
  "&:hover": {
    transform: "translateY(-2px)",
    boxShadow: "0 6px 20px rgba(90, 100, 119, 0.2)",
    background: `linear-gradient(135deg, rgba(251, 171, 53, 0.1) 0%, rgba(90, 100, 119, 0.1) 100%)`,
    borderColor: theme.palette.secondary.main,
    color: theme.palette.secondary.main,
  },
  "&:active": {
    transform: "translateY(0)",
    boxShadow: "0 2px 8px rgba(90, 100, 119, 0.1)",
  },
}));

// Danger Button - For destructive actions like Delete, Remove, etc.
export const DangerButton = styled(Button)(({ theme }) => ({
  borderRadius: theme.spacing(1),
  padding: theme.spacing(1.5, 3),
  fontWeight: 600,
  textTransform: "none",
  fontSize: "1rem",
  transition: "all 0.3s ease-in-out",
  background: "linear-gradient(135deg, #f44336 0%, #d32f2f 100%)",
  color: "#FFFFFF",
  border: "none",
  boxShadow: "0 4px 12px rgba(244, 67, 54, 0.3)",
  "&:hover": {
    transform: "translateY(-2px)",
    boxShadow: "0 6px 20px rgba(244, 67, 54, 0.4)",
    background: "linear-gradient(135deg, #d32f2f 0%, #b71c1c 100%)",
  },
  "&:active": {
    transform: "translateY(0)",
    boxShadow: "0 2px 8px rgba(244, 67, 54, 0.2)",
  },
}));

// Success Button - For positive actions like Save, Confirm, etc.
export const SuccessButton = styled(Button)(({ theme }) => ({
  borderRadius: theme.spacing(1),
  padding: theme.spacing(1.5, 3),
  fontWeight: 600,
  textTransform: "none",
  fontSize: "1rem",
  transition: "all 0.3s ease-in-out",
  background: "linear-gradient(135deg, #4caf50 0%, #388e3c 100%)",
  color: "#FFFFFF",
  border: "none",
  boxShadow: "0 4px 12px rgba(76, 175, 80, 0.3)",
  "&:hover": {
    transform: "translateY(-2px)",
    boxShadow: "0 6px 20px rgba(76, 175, 80, 0.4)",
    background: "linear-gradient(135deg, #388e3c 0%, #2e7d32 100%)",
  },
  "&:active": {
    transform: "translateY(0)",
    boxShadow: "0 2px 8px rgba(76, 175, 80, 0.2)",
  },
}));

// Modern Icon Button
export const ModernIconButton = styled(IconButton)(({ theme }) => ({
  borderRadius: theme.spacing(1),
  padding: theme.spacing(1),
  transition: "all 0.3s ease-in-out",
  backgroundColor: "rgba(90, 100, 119, 0.1)",
  color: theme.palette.primary.main,
  "&:hover": {
    backgroundColor: "rgba(251, 171, 53, 0.1)",
    color: theme.palette.secondary.main,
    transform: "translateY(-2px)",
    boxShadow: "0 4px 12px rgba(90, 100, 119, 0.2)",
  },
  "&:active": {
    transform: "translateY(0)",
  },
}));

// Floating Action Button with custom styling
export const ModernFab = styled(Fab)(({ theme }) => ({
  background: `linear-gradient(135deg, ${theme.palette.secondary.main} 0%, rgba(251, 171, 53, 0.8) 100%)`,
  color: "#FFFFFF",
  boxShadow: "0 6px 20px rgba(251, 171, 53, 0.4)",
  "&:hover": {
    background: `linear-gradient(135deg, rgba(251, 171, 53, 0.9) 0%, ${theme.palette.secondary.main} 100%)`,
    transform: "scale(1.1)",
    boxShadow: "0 8px 25px rgba(251, 171, 53, 0.5)",
  },
  "&:active": {
    transform: "scale(1.05)",
  },
}));

// Ghost Button - Minimal style
export const GhostButton = styled(Button)(({ theme }) => ({
  borderRadius: theme.spacing(1),
  padding: theme.spacing(1, 2),
  fontWeight: 500,
  textTransform: "none",
  fontSize: "0.95rem",
  transition: "all 0.3s ease-in-out",
  background: "transparent",
  border: "none",
  color: theme.palette.primary.main,
  "&:hover": {
    backgroundColor: "rgba(90, 100, 119, 0.05)",
    color: theme.palette.secondary.main,
  },
}));

// Button Group Container
export const ModernButtonGroup = styled("div")(({ theme }) => ({
  display: "flex",
  gap: theme.spacing(2),
  alignItems: "center",
  justifyContent: "center",
  flexWrap: "wrap",
  [theme.breakpoints.down("sm")]: {
    gap: theme.spacing(1),
    "& .MuiButton-root": {
      minWidth: "100px",
      fontSize: "0.875rem",
    },
  },
}));

// Loading Button Component
interface LoadingButtonProps {
  loading?: boolean;
  children: ReactNode;
  onClick?: () => void;
  variant?: "primary" | "secondary" | "danger" | "success";
  disabled?: boolean;
  type?: "button" | "submit" | "reset";
}

export const LoadingButton = ({
  loading = false,
  children,
  onClick,
  variant = "primary",
  disabled = false,
  type = "button",
  ...props
}: LoadingButtonProps) => {
  const ButtonComponent = {
    primary: PrimaryButton,
    secondary: SecondaryButton,
    danger: DangerButton,
    success: SuccessButton,
  }[variant];

  return (
    <ButtonComponent
      onClick={onClick}
      disabled={disabled || loading}
      type={type}
      {...props}
    >
      {loading ? "Loading..." : children}
    </ButtonComponent>
  );
};
